import { useEffect, useRef, useState } from 'react';

import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';

import './JsonViewer.scss';

const copyToClipboard = async (data: any) => {
  try {
    const jsonString = JSON.stringify(
      data,
      (key, value) => {
        // If the value is an object (but not an array), return an object with sorted keys
        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          const sorted: Record<string, any> = {};
          Object.keys(value)
            .sort()
            .forEach((k) => {
              sorted[k] = value[k];
            });
          return sorted;
        }
        return value;
      },
      2,
    );
    await navigator.clipboard.writeText(jsonString);
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
  }
};

interface JsonViewerProps {
  data: any;
  name?: string;
  defaultExpanded?: boolean;
  level?: number;
  expandSignal?: number;
}

export default function JsonViewer({
  data,
  name,
  defaultExpanded = false,
  level = 0,
  expandSignal,
}: JsonViewerProps) {
  // --- All hooks and state variables collected here ---
  const [expanded, setExpanded] = useState<boolean>(
    level === 0 ? true : defaultExpanded,
  );
  const lastExpandSignal = useRef<number | undefined>(undefined);
  // Only used for objects, but must be declared here for hooks order
  const [expandAllSignal, setExpandAllSignal] = useState<number>(0);

  // If expandSignal changes, expand this node (but only once per signal)
  useEffect(() => {
    if (expandSignal && expandSignal !== lastExpandSignal.current) {
      if (!expanded) setExpanded(true);
      lastExpandSignal.current = expandSignal;
    }
  }, [expandSignal, expanded]);

  if (data === null) {
    return (
      <div className="json_viewer_line">
        {name && <span className="json_key">{name}: </span>}
        <span className="json_null">null</span>
      </div>
    );
  }

  if (data === undefined) {
    return (
      <div className="json_viewer_line">
        {name && <span className="json_key">{name}: </span>}
        <span className="json_undefined">undefined</span>
      </div>
    );
  }

  const dataType = typeof data;

  // Primitive types
  if (dataType === 'string') {
    return (
      <div className="json_viewer_line">
        {name && <span className="json_key">{name}: </span>}
        <span className="json_string">"{data}"</span>
      </div>
    );
  }

  if (dataType === 'number') {
    return (
      <div className="json_viewer_line">
        {name && <span className="json_key">{name}: </span>}
        <span className="json_number">{data}</span>
      </div>
    );
  }

  if (dataType === 'boolean') {
    return (
      <div className="json_viewer_line">
        {name && <span className="json_key">{name}: </span>}
        <span className="json_boolean">{data.toString()}</span>
      </div>
    );
  }

  // Arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <div className="json_viewer_line">
          {name && <span className="json_key">{name}: </span>}
          <span className="json_bracket">[]</span>
        </div>
      );
    }

    return (
      <div className="json_viewer_object">
        <div className="json_viewer_line json_viewer_toggle">
          <div
            className="json_viewer_toggle_area"
            onClick={() => setExpanded(!expanded)}
          >
            <Icon
              symbol={expanded ? IconSymbol.down : IconSymbol.right}
              size={12}
              className="json_viewer_icon"
            />
            {name && <span className="json_key">{name}: </span>}
            <span className="json_bracket">[</span>
            {!expanded && (
              <span className="json_preview">
                {data.length} {data.length === 1 ? 'item' : 'items'}
              </span>
            )}
            {!expanded && <span className="json_bracket">]</span>}
          </div>
          <button
            className="json_viewer_copy_btn"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(data);
            }}
            title="Copy to clipboard"
          >
            <Icon symbol={IconSymbol.copy} size={14} />
          </button>
        </div>
        {expanded && (
          <div className="json_viewer_children">
            {data.map((item, index) => (
              <JsonViewer
                key={index}
                data={item}
                name={`[${index}]`}
                defaultExpanded={false}
                level={level + 1}
              />
            ))}
            <div className="json_viewer_line">
              <span className="json_bracket">]</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Objects
  if (dataType === 'object') {
    const keys = Object.keys(data).sort();
    if (keys.length === 0) {
      return (
        <div className="json_viewer_line">
          {name && <span className="json_key">{name}: </span>}
          <span className="json_bracket">{'{}'}</span>
        </div>
      );
    }

    return (
      <div className="json_viewer_object">
        <div className="json_viewer_line json_viewer_toggle">
          <div
            className="json_viewer_toggle_area"
            onClick={() => setExpanded(!expanded)}
            onDoubleClick={() => {
              const now = Date.now();
              setExpandAllSignal(now);
              setExpanded(true);
            }}
          >
            <Icon
              symbol={expanded ? IconSymbol.down : IconSymbol.right}
              size={12}
              className="json_viewer_icon"
            />
            {name && <span className="json_key">{name}: </span>}
            <span className="json_bracket">{'{'}</span>
            {!expanded && (
              <span className="json_preview">
                {keys.length} {keys.length === 1 ? 'key' : 'keys'}
              </span>
            )}
            {!expanded && <span className="json_bracket">{'}'}</span>}
          </div>
          <button
            className="json_viewer_copy_btn"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(data);
            }}
            title="Copy to clipboard"
          >
            <Icon symbol={IconSymbol.copy} size={14} />
          </button>
        </div>
        {expanded && (
          <div className="json_viewer_children">
            {keys.map((key) => (
              <JsonViewer
                key={key}
                data={data[key]}
                name={key}
                defaultExpanded={false}
                level={level + 1}
                expandSignal={expandAllSignal || expandSignal}
              />
            ))}
            <div className="json_viewer_line">
              <span className="json_bracket">{'}'}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback for functions and other types
  return (
    <div className="json_viewer_line">
      {name && <span className="json_key">{name}: </span>}
      <span className="json_other">{String(data)}</span>
    </div>
  );
}
